import { Paths, ProjectPathParams } from '../../../../app/router/app.router';
import { KnownSortOrder } from '@groundfloor-platform/mgmt-service-g-sdk';
import {
  InfiniteScrollProps,
  InfiniteTable,
  PaginatedRow
} from '../../../../ui/components/pure/list-table-wrappers/infinite-table.component';

import { useDeliveriesTable } from '../hooks/use-deliveries-table.hook';
import { useCallback, useMemo } from 'react';
import { Delivery } from '../../models/delivery.model';

interface DeliveriesTableProps {
  params: ProjectPathParams<Paths.deliveries>;
  pages: Array<{ items: Delivery[] }> | PaginatedRow<Delivery>[];
  infiniteScroll: InfiniteScrollProps;
  setFormData: (form: any) => void;
  sortData?: { sortBy?: string; order?: KnownSortOrder; sortOrder?: KnownSortOrder } | undefined;
  isLoading: boolean;
  isPreviousData: boolean;
  hasError: boolean;
  showHubName?: boolean;
  columns?: { key: string; label: string }[];
  viewId?: string;
}

export const DeliveriesTable = ({
  params,
  pages,
  infiniteScroll,
  setFormData,
  sortData,
  isLoading,
  isPreviousData,
  hasError,
  showHubName = false,
  columns,
  viewId = 'main'
}: DeliveriesTableProps) => {
  // detect agents to hide/show agent column
  const dataContainsAgents = useMemo(
    () =>
      (pages as Array<{ items: Delivery[] }>)
        .flatMap((p) => p.items ?? [])
        .some((d) => (d as any)?.deliveredBy?.agent?.name !== undefined),
    [pages]
  );

  // build table with optional controlled columns
  const tb = useDeliveriesTable({
    projectID: params.projectID,
    hideAgentColumn: !dataContainsAgents,
    visibleColumns: columns
  });

  // namespace table-level persistence/caches
  const tableId = viewId === 'internal' ? `deliveries-${viewId}` : `deliveries-${viewId}-${params.projectID}`;
  // @ts-ignore
  tb?.id?.(tableId);
  if (columns) {
    // @ts-ignore
    tb?.persistence?.disable?.();
  }

  // map each row to its details page URL
  const mapRowURL = useCallback(
    (row: Delivery) => row.createDetailsPageUrl((params as any).shortcode || params.projectID),
    [params.projectID, (params as any).shortcode]
  );
  tb.config({ mapRowURL });
  tb.addPredicate('HUB', () => !!showHubName);

  const normalizedSortData = useMemo(() => {
    const defaultSort: { sortBy: string; sortOrder: KnownSortOrder } = {
      sortBy: 'createdTime',
      sortOrder: KnownSortOrder.Desc
    };
    const incomingBy = sortData?.sortBy;
    const incomingOrder = (sortData?.sortOrder ?? sortData?.order) as KnownSortOrder | undefined;
    if (!incomingBy || !incomingOrder) return defaultSort;
    return { sortBy: incomingBy, sortOrder: incomingOrder };
  }, [sortData]);

  // Translate table callback -> your form shape
  const handleSort = useCallback(
    (s: { sortBy: string; sortOrder: KnownSortOrder }) =>
      setFormData({ sortBy: s.sortBy, order: s.sortOrder, sortOrder: s.sortOrder }),
    [setFormData]
  );

  return (
    <InfiniteTable
      key={tableId}
      // @ts-ignore if supported
      storageKey={tableId}
      uniqueKey={'id'}
      onSort={handleSort}
      sortData={normalizedSortData}
      table={tb}
      hasError={hasError}
      pages={pages as any}
      infiniteScroll={infiniteScroll}
      isLoading={isLoading}
      isPreviousData={isPreviousData}
    />
  );
};
